import { OverlayRef, Overlay } from '@angular/cdk/overlay';
import { Injectable, Injector, signal, Type } from '@angular/core';
import { ComponentPortal } from '@angular/cdk/portal';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { getChatBotOverlayPosition, PositionType } from './chat-bot-overlay.helper';
import { UserActionIds, UserActionTrackerService } from '@miq/utils/metrics';

@UntilDestroy()
@Injectable()
export class ChatBotOverlayService {
  private overlayRef: OverlayRef | null = null;
  isChatBotOpen = signal(false);
  constructor(
    private overlay: Overlay,
    private injector: Injector,
    private userActionTrackerService: UserActionTrackerService
  ) {}

  toggle<T>(origin: HTMLElement, component: Type<T>, position?: PositionType) {
    if (this.overlayRef && this.overlayRef.hasAttached()) {
      this.closeChat();
    } else {
      this.openChat(origin, component, position);
    }
  }

  close() {
    if (this.overlayRef) {
      this.isChatBotOpen.update(prev => !prev);
      this.overlayRef.hostElement.classList.toggle('hidden');
      this.overlayRef.backdropElement.classList.toggle('hidden');
    }
  }

  isChatWindowOpen(): boolean {
    return this.isChatBotOpen();
  }

  openChat<T>(
    origin: HTMLElement,
    component: Type<T>,
    position?: PositionType
  ): { overlayRef: OverlayRef; componentRef: T } {
    if (this.overlayRef) this.overlayRef.dispose();
    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(origin)
      .withFlexibleDimensions(false)
      .withPush(false)
      .withPositions([getChatBotOverlayPosition(position)]);
    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
    });
    const portal = new ComponentPortal(component, null, this.injector);
    const componentRef = this.overlayRef.attach(portal).instance;
    this.isChatBotOpen.set(true);
    this.trackUserAction('Chat window opened');
    this.overlayRef
      .backdropClick()
      .pipe(untilDestroyed(this))
      .subscribe(() => this.closeChat());
    return {
      overlayRef: this.overlayRef,
      componentRef: componentRef as T,
    };
  }

  closeChat() {
    this.overlayRef?.dispose();
    this.overlayRef = null;
    this.isChatBotOpen.set(false);
    this.trackUserAction('Chat window closed');
  }

  private trackUserAction(actionTaken: string): void {
    const actionId = UserActionIds.DigitalAssistant;
    const actionDetails = {
      actionId,
      actionTaken: actionTaken,
    };
    this.userActionTrackerService.registerAction({ actionId, actionDetails });
  }
}
